import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../LanguageContext';
import { useTheme } from '../ThemeContext';
import { usePortfolioZoomScroll, getPortfolioPinTranslateYPercent } from '../hooks/usePortfolioZoomScroll';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { getGridZoomTrackVh, getGridSectionBottomSpacerVh, PORTFOLIO_ZOOM_HANDOFF_PROGRESS } from '../portfolioGridData';
import PortfolioGrid from './PortfolioGrid';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);

    const update = () => setIsMobile(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);

    return () => mediaQuery.removeEventListener('change', update);
  }, [breakpoint]);

  return isMobile;
}

const PortfolioSection = () => {
  const { language } = useLanguage();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const reducedMotion = usePrefersReducedMotion();
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const {
    showPinOverlay,
    zoom1Progress,
    middleZoomProgress,
    captionProgress,
    zoom2Progress,
    gridReturnProgress,
  } = usePortfolioZoomScroll(sectionRef, trackRef, isMobile);

  const pinOverlayTranslateY = getPortfolioPinTranslateYPercent(gridReturnProgress);
  const pinOverlayOffscreen = pinOverlayTranslateY <= -99.5;
  const hideInlineGrid = showPinOverlay && zoom1Progress > PORTFOLIO_ZOOM_HANDOFF_PROGRESS;

  return (
    <section
      ref={sectionRef}
      id="portfolio"
      className={`relative z-10 ${theme.colors.bg}`}
      style={{ marginLeft: 'calc(50% - 50vw)' }}
      aria-label={language === 'de' ? 'Portfolio' : 'Portfolio'}
    >
      <div
        className={`h-screen w-screen ${theme.colors.bg}`}
        style={{ visibility: hideInlineGrid ? 'hidden' : 'visible' }}
      >
        <PortfolioGrid
          isMobile={isMobile}
          zoom1Progress={0}
          middleZoomProgress={0}
          captionProgress={0}
          zoom2Progress={0}
          gridReturnProgress={0}
          reducedMotion={reducedMotion}
        />
      </div>

      <div
        ref={trackRef}
        data-portfolio-scroll-track
        aria-hidden
        className={`w-screen ${theme.colors.bg}`}
        style={{ height: `${getGridZoomTrackVh(isMobile)}vh` }}
      />

      <div
        aria-hidden
        className={`w-screen ${theme.colors.bg}`}
        style={{ height: `${getGridSectionBottomSpacerVh(isMobile)}vh` }}
      />

      <div
        className={`fixed inset-0 z-50 overflow-visible ${showPinOverlay ? theme.colors.bg : ''}`}
        style={{
          transform: `translateY(${pinOverlayTranslateY}vh)`,
          willChange: showPinOverlay ? 'transform' : undefined,
          pointerEvents: 'none',
          visibility: showPinOverlay && !pinOverlayOffscreen ? 'visible' : 'hidden',
        }}
      >
        <PortfolioGrid
          isMobile={isMobile}
          zoom1Progress={showPinOverlay ? zoom1Progress : 0}
          middleZoomProgress={showPinOverlay ? middleZoomProgress : 0}
          captionProgress={showPinOverlay ? captionProgress : 0}
          zoom2Progress={showPinOverlay ? zoom2Progress : 0}
          gridReturnProgress={showPinOverlay ? gridReturnProgress : 0}
          reducedMotion={reducedMotion}
        />
      </div>
    </section>
  );
};

export default PortfolioSection;
